import { ColDef } from "@ag-grid-community/core";
import Decimal from "decimal.js";
import { useFeeBasisRetrieveSuspense } from "gen/endpoints/fee-basis/fee-basis";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useNamedDateListSuspense } from "gen/endpoints/named-dates/named-dates";
import { useTransactionCodeConfigListSuspense } from "gen/endpoints/transactions/transactions";
import { FeeCreateAPIDataRequest } from "gen/model/feeCreateAPIDataRequest";
import { NamedDateData } from "gen/model/namedDateData";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatPercentageForTable } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const ReadOnlyFeeForm: React.FC<{
  fundFamilyId: string;
  feeData: FeeCreateAPIDataRequest;
}> = ({ fundFamilyId, feeData }) => {
  const { t } = useTranslation(["fees", "common", "enums"]);
  const { data: transactionCodeData } =
    useTransactionCodeConfigListSuspense(fundFamilyId);
  const { data: fundEntitiesData } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
  });

  return (
    <>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item label={t("fees:cadence")}>
                {t(`enums:FeeCadenceEnum.${feeData.fee.cadence}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t("fees:dayCount")}>
                {t(`enums:DayCountEnum.${feeData.fee.day_count}`)}
              </Descriptions.Item>
              {feeData.fee.partial_period_day_count && (
                <Descriptions.Item label={t("fees:partialPeriodDayCount")}>
                  {t(
                    `enums:DayCountEnum.${feeData.fee.partial_period_day_count}`,
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("fees:feeType")}>
                {t(`enums:FeeTypeEnum.${feeData.fee.fee_type}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t("fees:chargeTiming.title")}>
                {t(`enums:ChargeTimingEnum.${feeData.fee.charge_timing}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t("fees:feeBasis")}>
                {t(
                  `enums:FeeBasisDeterminationEnum.${feeData.fee.fee_basis_determination}`,
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("fees:feeBasisPartialPeriod")}>
                {t(
                  `enums:FeePartialPeriodBasisDeterminationEnum.${feeData.fee.partial_period_basis_determination}`,
                )}
              </Descriptions.Item>
              {feeData.fee.true_up_methodology && (
                <Descriptions.Item label={t("fees:trueUpMethodology")}>
                  {t(
                    `enums:FeeBasisDeterminationEnum.${feeData.fee.true_up_methodology}`,
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("fees:transactionCodeCall")}>
                {
                  transactionCodeData.find(
                    (t) =>
                      t.transaction_code_id ===
                      feeData.fee.call_transaction_code_id,
                  )?.name
                }
              </Descriptions.Item>
              <Descriptions.Item label={t("common:funds")}>
                {feeData.applies_to_all_funds
                  ? t("fees:feeForm.appliesToAllFunds")
                  : feeData.fund_ids
                      ?.map(
                        (id) =>
                          fundEntitiesData.find((fund) => fund.id === id)
                            ?.short_name,
                      )
                      .join(", ")}
              </Descriptions.Item>
              {feeData.fee.initial_fee_charge_timing && (
                <Descriptions.Item label={t("fees:initialChargeTiming")}>
                  {t(
                    `enums:ChargeTimingEnum.${feeData.fee.initial_fee_charge_timing}`,
                  )}
                </Descriptions.Item>
              )}
              {feeData.fee.initial_fee_basis_determination && (
                <Descriptions.Item
                  label={t("fees:initialFeeBasisDetermination")}
                >
                  {t(
                    `enums:FeeBasisDeterminationEnum.${feeData.fee.initial_fee_basis_determination}`,
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item
                label={t("fees:chargeInterestToExistingCommitments")}
              >
                {feeData.fee.interest_charged_to_existing_commitments
                  ? t("common:yes")
                  : t("common:yes")}
              </Descriptions.Item>
              <Descriptions.Item label={t("fees:catchUpMaterialityThreshold")}>
                {formatMoney(feeData.fee.catch_up_materiality_threshold)}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      <CalculationsTable fundFamilyId={fundFamilyId} feeData={feeData} />
    </>
  );
};

const CalculationsTable: React.FC<{
  fundFamilyId: string;
  feeData: FeeCreateAPIDataRequest;
}> = ({ fundFamilyId, feeData }) => {
  const { t } = useTranslation(["fees", "common", "enums"]);

  const { data: namedDateData } = useNamedDateListSuspense(fundFamilyId);
  const { data: feeBasisData } = useFeeBasisRetrieveSuspense(fundFamilyId);

  const rowData = useMemo(
    () =>
      feeData.fee_calculations.flatMap((calc) =>
        calc.fee_parts.flatMap(
          (part, partIndex) =>
            ({
              path: [calc.id!, partIndex.toString()] as string[],
              namedStartDate: calc.named_start_date_id,
              namedEndDate: calc.named_end_date_id,
              customStartDate: calc.custom_start_date,
              customEndDate: calc.custom_end_date,
              feeBasisId: part.fee_basis_id,
              feeRate: part.fee_rate,
              feeCalculationId: part.fee_calculation_id,
            }) as const,
        ),
      ),
    [feeData],
  );

  const columnDefs = useMemo<ColDef<(typeof rowData)[number]>[]>(
    () => [
      {
        colId: "startDate",
        headerName: t("fees:namedDates.startDate"),
        valueFormatter: (props) =>
          formatNamedOrCustomDate(
            props.data?.namedStartDate,
            props.data?.customStartDate,
            namedDateData,
          ),
      },
      {
        colId: "endDate",
        headerName: t("fees:namedDates.endDate"),
        valueFormatter: (props) =>
          formatNamedOrCustomDate(
            props.data?.namedEndDate,
            props.data?.customEndDate,
            namedDateData,
          ),
      },
      {
        colId: "feeBasis",
        headerName: t("fees:basis"),
        valueFormatter: (props) =>
          feeBasisData.find((b) => b.id === props.data?.feeBasisId)?.name ?? "",
      },
      {
        field: "feeRate",
        headerName: t("fees:feeRate"),
        valueFormatter: (props) =>
          formatPercentageForTable(
            new Decimal(props.data?.feeRate ?? 0).mul(100).valueOf(),
          ),
      },
    ],
    [t, namedDateData, feeBasisData],
  );

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>{t("fees:feeCalculations.title")}</TableBoxTitle>
      </TableBoxHeader>
      <TableBoxBody>
        <MaybernTable
          domLayout="autoHeight"
          columnDefs={columnDefs}
          rowData={rowData}
          onFirstDataRendered={(params) => {
            params.api.autoSizeAllColumns();
          }}
        />
      </TableBoxBody>
    </TableBox>
  );
};

const formatNamedOrCustomDate = (
  namedDateId: string | undefined | null,
  customDate: string | undefined | null,
  namedDateData: NamedDateData[],
) =>
  namedDateData.find((namedDate) => namedDate.id === namedDateId)?.name ||
  formatIsoDateToDisplay(customDate) ||
  "";
